import { Fragment } from "react";
import { View } from "react-native";
import { GatheringPreviewDetailItem } from "src/components/GatheringPreviewRow/types";
import NotificationDot from "src/components/NotificationDot";
import abbreviateBigNumber from "src/helpers/abbreviateBigNumber";
import parseV0TemplateUrl from "src/helpers/parseV0TemplateUrl";
import IconMessages from "src/icons/IconMessages";
import IconPhoto from "src/icons/IconPhoto";
import IconUsers from "src/icons/IconUsers";
import { HomeGatheringItemType } from "src/screens/(internal)/(home)/Home/types";
import { enumNever } from "src/shared/helpers/generalHelpers";
import { NonOptional } from "src/shared/types/general";
import { radiusSmall } from "src/styles/radius";
import { paddingSmall } from "src/styles/spacing";
import { Image, Link, Pill, Text } from "src/swsh-native";
import { GatheringPreviewCardProps } from "../types";

const renderDetail = ({
	detail,
	item,
	index,
}: {
	detail: GatheringPreviewDetailItem | null | undefined;
	item: HomeGatheringItemType;
	index: number;
}) => {
	if (detail == null) {
		return null;
	}
	if (typeof detail === "function") {
		return (
			<Fragment key={index}>
				{detail({
					item,
				})}
			</Fragment>
		);
	}
	switch (detail) {
		case "numParticipants":
			if (item.numParticipants == null || item.numParticipants <= 0) {
				return null;
			}
			return (
				<Pill
					sizeVariant="sm"
					key={`${detail}-${index}`}
					pointerEvents="none"
					Icon={IconUsers}
				>{`${abbreviateBigNumber(item.numParticipants)}`}</Pill>
			);
		case "numPhotos":
			if (item.numPhotos == null || item.numPhotos <= 0) {
				return null;
			}
			return (
				<Pill
					sizeVariant="sm"
					key={`${detail}-${index}`}
					pointerEvents="none"
					Icon={IconPhoto}
				>{`${abbreviateBigNumber(item.numPhotos)}`}</Pill>
			);
		case "community":
			const communityThumbnailUrl = parseV0TemplateUrl({
				readUrl: item.community?.thumbnail?.readUrl,
				aspectRatio: "1x1",
				size: "xs",
				strategy: "cover",
			});
			const communityRefCode = item.community?.refCode;
			if (!communityThumbnailUrl || !communityRefCode) {
				return null;
			}
			return (
				<Link
					testID="GatheringCommunityPreview"
					href={`/group/${communityRefCode}`}
					key={`${detail}-${index}`}
					style={{
						borderRadius: radiusSmall,
					}}
				>
					<Image
						alt="Group Thumbnail"
						key={`${detail}-${index}`}
						src={communityThumbnailUrl}
						dim={{
							width: 22,
							height: 22,
						}}
						style={{
							borderRadius: radiusSmall / 2,
						}}
					/>
				</Link>
			);
		case "numComments":
			if (item.numComments == null || item.numComments <= 0) {
				return null;
			}
			return (
				<NotificationDot
					size={8}
					enabled={item.hasNewComments ?? false}
					key={`${detail}-${index}`}
				>
					<Pill
						testID="NumComments"
						sizeVariant="sm"
						pointerEvents="none"
						Icon={IconMessages}
					>{`${abbreviateBigNumber(item.numComments)}`}</Pill>
				</NotificationDot>
			);

		default:
			return enumNever(detail);
	}
};

const GatheringPreviewInfo = ({
	gathering,
	details,
	href,
}: {
	gathering: HomeGatheringItemType;
	href: string;
} & NonOptional<Pick<GatheringPreviewCardProps, "details">>) => {
	const hasLongName = (gathering.name ?? "").length > 20;
	return (
		<View
			pointerEvents="box-none"
			style={{
				flex: 1,
			}}
		>
			<Link href={href}>
				<Text
					testID="Name"
					semiLarge={!hasLongName}
					basic={hasLongName}
					bold
					numberOfLines={1}
				>
					{gathering.name}
				</Text>
			</Link>
			<View
				pointerEvents="box-none"
				style={{
					marginTop: paddingSmall,
					flexDirection: "row",
					flexWrap: "wrap",
					alignItems: "center",
					columnGap: paddingSmall,
					rowGap: paddingSmall,
				}}
			>
				{details.map((detail, index) => renderDetail({ detail, item: gathering, index }))}
			</View>
		</View>
	);
};
export default GatheringPreviewInfo;
